/* eslint-disable max-lines-per-function */
import { useState, useRef, useEffect } from "react";
import { cn } from "tailwind-variants";
import initProductSavedCartsSelector from "components/jiffy/product-saved-carts-selector/product-saved-carts-selector";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import TransfersStoreCredit from "./common/TransfersStoreCredit.vue";
import Spinner from "components/jiffy/react-cart/Spinner.vue";
import Artwork from "./Artwork.vue";
import Summary from "./Summary.vue";
import UploadBox from "./UploadBox.vue";
import DtfAddToCart from "./DtfAddToCart.vue";
import DeliveryDate from "./DeliveryDate.vue";
import PreCut from "./PreCut.vue";
import DesignServicesSubscribe from "./ArtworkQualityConfigPanel/DesignServices/DesignServicesSubscribe.vue";
import { useDtfStore } from "./store";
import "./MultiArtworkModal.scss";
import { renderList } from "common/vue/render-list";

function MultiArtworkModal({
  moveBellow = false,
  currentStore = null,
  activeConfigSection = "jiffy-print-perfect",
  onUpload,
  slots,
  ...restProps
}) {
  const [preventClose, setPreventClose] = useState(true);
  const [isSameDay, setIsSameDay] = useState(false);
  const deliveryDateObserverRef = useRef(null);
  const modalRef = useRef(null);

  const dtfStore = useDtfStore();

  function showModal() {
    return dtfStore.showModal;
  }

  function artworks() {
    return dtfStore.artworks;
  }

  function files() {
    return dtfStore.files;
  }

  function loadedArtworks() {
    return dtfStore.loadedArtworks;
  }

  function withPreCut() {
    return dtfStore.withPreCut;
  }

  function isSticker() {
    return dtfStore.isSticker;
  }

  function isGuest() {
    return dtfStore.isGuest;
  }

  function storeCreditInfo() {
    return dtfStore.storeCreditInfo;
  }

  function blankItemsCount() {
    return dtfStore.blankItemsCount;
  }

  function isBogoApplicable() {
    return dtfStore.isBogoApplicable;
  }

  function isNewToTransfers() {
    return dtfStore.isNewToTransfers;
  }

  function uploadedCount() {
    return loadedArtworks().length + 1;
  }

  function pendingFiles() {
    return files().filter(({ uploadError }) => !uploadError);
  }

  function filesUploadedMessage() {
    if (pendingFiles().length === files().length) {
      return "All files successfully uploaded";
    }
    return "Files have been uploaded";
  }

  function showStoreCredit() {
    return !isSticker() && !isGuest() && Number(storeCreditInfo().orderSubtotal) > 0;
  }

  function showFreeTransfersBadge() {
    if (!isBogoApplicable()) {
      return false;
    }
    const allArtworksReady = artworks().every(artwork => !artwork.isLoading && !artwork.isFile);
    if (!allArtworksReady) {
      return false;
    }
    if (blankItemsCount() <= 0) {
      return false;
    }
    if (isGuest()) {
      return true;
    }
    if (isNewToTransfers() === null || isNewToTransfers() === undefined) {
      return false;
    }
    return isNewToTransfers() === true;
  }

  function getModal() {
    return modalRef.current;
  }

  function getDeliveryDateTarget() {
    const modal = getModal();
    return modal?.querySelector(".js-product-details-delivery-dates-simple-direct-value");
  }

  function getDeliveryDateText() {
    const target = getDeliveryDateTarget();
    return target ? target.textContent.trim().toLowerCase() : "";
  }

  function updateIsSameDay() {
    const dateText = getDeliveryDateText();
    const sameDayValue = dateText.includes("today");
    setIsSameDay(sameDayValue);
  }

  function setupDeliveryDateObserver() {
    const target = getDeliveryDateTarget();
    if (target) {
      updateIsSameDay();
      const observer = new MutationObserver(() => updateIsSameDay());
      observer.observe(target, {
        childList: true,
        subtree: true
      });
      deliveryDateObserverRef.current = observer;
    }
  }

  function setShowDiscardModal(...args) {
    return dtfStore.setShowDiscardModal(...args);
  }

  function updatePdpDates(...args) {
    return dtfStore.updatePdpDates(...args);
  }

  function onShow() {
    const $modal = $(getModal());
    $modal
      .modal("show")
      .on("hide.bs.modal", event => {
        if (showModal() && preventClose && event.target.id === $modal[0].id) {
          event.preventDefault();
          onClose();
        }
      })
      .on("hidden.bs.modal", () => {
        $modal.off("hidden.bs.modal");
        document.body.classList.remove("dtf-modal-open");
      });
    document.body.classList.add("dtf-modal-open");
  }

  function onClose() {
    setShowDiscardModal();
  }

  function handleUpload() {
    onUpload?.();
  }

  useEffect(() => {
    onShow();
    setupDeliveryDateObserver();
    queueMicrotask(() => {
      window.JiffyDeliverTo.reloadToggle();
    });
    initProductSavedCartsSelector();
    updatePdpDates();

    return () => {
      deliveryDateObserverRef.current?.disconnect();
      setPreventClose(false);
      $(getModal()).modal("hide");
    };
  }, []);

  const { className: inheritedClassName, ...otherProps } = restProps;

  return (
    <div
      {...otherProps}
      className={cn(
        "modal multi-artwork-modal scope-fd566941",
        moveBellow && "multi-artwork-modal--bellow",
        inheritedClassName
      )}
      id="multi-artwork-modal"
      ref={modalRef}
      role="dialog"
      data-testid="modal"
    >
      <div className="multi-artwork-modal__modal-box modal-dialog scope-fd566941">
        <div
          className="multi-artwork-modal__modal-header scope-fd566941"
          role="heading"
          aria-label="Your files"
          tabIndex="0"
          data-testid="multi-artwork-modal-modal-header"
        >
          <h3 className="multi-artwork-modal__modal-title scope-fd566941">
            {pendingFiles().length ? (
              <>
                <Spinner
                  className="multi-artwork-modal__top-icon scope-fd566941"
                  size="small"
                  data-testid="multi-artwork-modal-top-icon"
                />{" "}
                Uploading {uploadedCount()} of {artworks().length} files{" "}
              </>
            ) : (
              <>
                <div className="scope-fd566941">
                  <UseSvg
                    className="multi-artwork-modal__top-icon scope-fd566941"
                    icon="check-circle"
                    data-testid="multi-artwork-modal-top-icon-1"
                  />
                </div>{" "}
                {filesUploadedMessage()}
              </>
            )}
          </h3>
          <button
            className="multi-artwork-modal__modal-button-close scope-fd566941"
            type="button"
            onClick={onClose}
            data-testid="multi-artwork-modal-modal-button-close"
          >
            <UseSvg className="scope-fd566941" icon="modal-close-big" data-testid="use-svg" />
          </button>
        </div>
        <div className="multi-artwork-modal__modal-body scope-fd566941">
          <div className="multi-artwork-modal__artworks scope-fd566941">
            {renderList(artworks(), (item, index) => (
              <Artwork
                className="scope-fd566941"
                key={index + String(item.id)}
                defaultActiveConfigSection={index === 0 ? activeConfigSection : undefined}
                artwork={item}
                index={index}
                isLoading={item.isLoading}
                currentStore={currentStore}
                isMultiUpload={true}
                showFreeTransfersBadge={showFreeTransfersBadge()}
                showFullScreenButton={true}
                data-testid="artwork"
              />
            ))}
            {!files().length ? (
              <UploadBox
                className="scope-fd566941"
                onUpload={handleUpload}
                data-testid="upload-box"
              />
            ) : null}
          </div>
          <div className="multi-artwork-modal__summary scope-fd566941">
            <div className="multi-artwork-modal__delivery-date scope-fd566941">
              <DeliveryDate
                className="scope-fd566941"
                isSameDay={isSameDay}
                data-testid="delivery-date"
              />
            </div>
            {slots?.["free-shipping"]?.()}
            {withPreCut() ? (
              <PreCut className="scope-fd566941" isModal={true} data-testid="pre-cut" />
            ) : null}
            <DesignServicesSubscribe
              className="scope-fd566941"
              data-testid="design-services-subscribe"
            />

            <Summary className="scope-fd566941" data-testid="summary" />
            {showStoreCredit() ? (
              <TransfersStoreCredit
                className="scope-fd566941"
                storeCreditInfo={storeCreditInfo()}
                data-testid="transfers-store-credit"
              />
            ) : null}
            {slots?.["saved-carts"]?.()}
            <DtfAddToCart className="scope-fd566941" data-testid="dtf-add-to-cart" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiArtworkModal;
